import * as React from "react"
import {
  Button,
  Heading,
  StyledPanelBodySection,
  StyledPanelBoilerplate,
  ThemeCss,
  Tooltip,
} from "gatsby-interface"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import { RemoveDomainForm } from "./RemoveDomainForm"
import { GatsbyCloudCdnFeatures } from "./GatsbyCloudCdnFeatures"
import { HostingDetails, HostingDomain } from "@modules/graphql/types"
import { AddDomainForm } from "./AddDomainForm"
import { PrimarySecondaryCombo } from "./PrimarySecondaryCombo"
import { MakeDomainPrimaryForm } from "./MakeDomainPrimaryForm"
import { RequestCertificateForm } from "./RequestCertificateForm"
import { useTracker, SegmentEventType } from "@modules/analytics"
import { interpolateMessage } from "@modules/locales"
import { useFlags } from "@modules/featureFlags"

const headerCss: ThemeCss = theme => ({
  alignItems: `center`,
  borderBottom: `1px solid ${theme.colors.grey[20]}`,
  display: `flex`,
  justifyContent: `space-between`,
  paddingBottom: theme.space[2],
  marginTop: theme.space[11],
})

const headingCss: ThemeCss = theme => ({
  fontFamily: theme.fonts.body,
  fontSize: theme.fontSizes[2],
  fontWeight: theme.fontWeights.semiBold,
})

const lightHeadingCss: ThemeCss = theme => ({
  margin: `${theme.space[6]} 0 ${theme.space[3]}`,
  textTransform: `uppercase`,
  fontWeight: theme.fontWeights.body,
  color: theme.colors.grey[60],
  letterSpacing: theme.letterSpacings.tracked,
  fontSize: theme.fontSizes[0],

  "&:first-of-type": {
    marginTop: 0,
  },
})

const listCss: ThemeCss = theme => ({
  margin: 0,
  listStyleType: `none`,
})

const formDescriptionCss: ThemeCss = theme => ({
  marginTop: theme.space[3],
  marginBottom: theme.space[6],
})

const fakeDisabledAddButtonCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[0],
  color: theme.colors.purple[60],
  opacity: 0.5,
  cursor: `not-allowed`,
})

export type ConnectedDomainsProps = {
  hostingDetails: HostingDetails
  siteId: string
  organizationId: string
  allowToEdit?: boolean
  showForm?: boolean
  onClose?: () => void
  onSuccess?: () => void
}

const CUSTOM_DOMAINS_LIMIT = 20

export function ConnectedDomains({
  hostingDetails,
  siteId,
  organizationId,
  allowToEdit,
  showForm: showDomainFormFromParent,
  onClose: onCloseCallback,
  onSuccess,
  ...rest
}: ConnectedDomainsProps) {
  const { flags } = useFlags()
  const { trackSegment, trackAction } = useTracker()
  const [showDomainForm, setShowDomainForm] = React.useState<boolean>(false)
  const [domainToRemove, setDomainToRemove] = React.useState<string | null>(
    null
  )
  const [domainToPrimary, setDomainToPrimary] = React.useState<string | null>(
    null
  )

  React.useEffect(() => {
    if (showDomainFormFromParent) {
      setShowDomainForm(showDomainFormFromParent)
    }
  }, [showDomainFormFromParent])

  React.useEffect(() => {
    if (showDomainForm === false && onCloseCallback) {
      onCloseCallback()
    }
  }, [showDomainForm])

  const [
    manageCertificateForDomain,
    setManageCertificateForDomain,
  ] = React.useState<string | null>(null)

  const domains = hostingDetails.domains ?? []

  const { primaryDomains, secondaryDomains } = domains.reduce<{
    primaryDomains: HostingDomain[]
    secondaryDomains: Record<string, HostingDomain>
  }>(
    (acc, cur) => {
      if (cur.primaryDomainId && !cur.cloudCdnDefault) {
        acc.secondaryDomains[cur.primaryDomainId] = cur
      } else {
        acc.primaryDomains.push(cur)
      }

      return acc
    },
    {
      primaryDomains: [],
      secondaryDomains: {},
    }
  )

  const [defaultPrimaryDomain, ...customPrimaryDomains] = primaryDomains
  // to maintain unchanged order of primary domains, we order them alphabetically
  // excluding 'www.' prefix from order comparison
  const customPrimaryDomainsOrdered = customPrimaryDomains.sort((a, b) => {
    const A = a.id.replace(/^www\./, "")
    const B = b.id.replace(/^www\./, "")

    return A.localeCompare(B)
  })

  const primaryDomainsOrdered = [
    defaultPrimaryDomain,
    ...customPrimaryDomainsOrdered,
  ]

  const certificates = hostingDetails.certificates ?? []

  return (
    <div {...rest}>
      <div css={listCss}>
        {primaryDomainsOrdered.map((primaryDomain, idx) => {
          const primaryCertificate = certificates.find(cert => {
            return cert.domains?.find(dmn => dmn.id === primaryDomain.id)
          })

          const secondaryDomain = secondaryDomains[primaryDomain.id]

          const secondaryCertificate =
            secondaryDomain &&
            certificates.find(cert => {
              return cert.domains?.find(dmn => dmn.id === secondaryDomain.id)
            })

          return (
            <React.Fragment key={primaryDomain.id}>
              {idx === 0 && primaryDomain.cloudCdnDefault && (
                <Heading as="h4" css={lightHeadingCss}>
                  Default
                </Heading>
              )}

              {idx === 1 && !primaryDomain.cloudCdnDefault && (
                <Heading as="h4" css={lightHeadingCss}>
                  Custom
                </Heading>
              )}

              <PrimarySecondaryCombo
                key={primaryDomain.id}
                siteId={siteId}
                primaryDomain={primaryDomain}
                primaryDomainOptions={customPrimaryDomainsOrdered}
                primaryCertificate={primaryCertificate}
                secondaryDomain={secondaryDomain}
                secondaryCertificate={secondaryCertificate}
                onRemove={setDomainToRemove}
                onMakePrimary={setDomainToPrimary}
                onRequestCertificate={setManageCertificateForDomain}
                allowToEdit={allowToEdit}
              />
            </React.Fragment>
          )
        })}
      </div>

      {!!domainToRemove && (
        <RemoveDomainForm
          siteId={siteId}
          organizationId={organizationId}
          domain={domainToRemove}
          onDismiss={() => setDomainToRemove(null)}
          onComplete={() => {
            setDomainToRemove(null)

            onSuccess && onSuccess()

            trackAction({
              eventType: `TRACK_EVENT`,
              name: `Custom Domain removed`,
              uiSource: `Site Settings`,
            })
            trackSegment({
              type: SegmentEventType.Track,
              event: "Custom Domain removed",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {!!domainToPrimary && (
        <MakeDomainPrimaryForm
          siteId={siteId}
          domain={domainToPrimary}
          onDismiss={() => setDomainToPrimary(null)}
          onComplete={() => {
            setDomainToPrimary(null)

            trackAction({
              eventType: `TRACK_EVENT`,
              name: `Domain set as Primary`,
              uiSource: `Site Settings`,
            })
            trackSegment({
              type: SegmentEventType.Track,
              event: "Domain set as Primary",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {!!manageCertificateForDomain && (
        <RequestCertificateForm
          siteId={siteId}
          domain={manageCertificateForDomain}
          onDismiss={() => setManageCertificateForDomain(null)}
          onComplete={() => {
            setManageCertificateForDomain(null)

            trackAction({
              eventType: `TRACK_EVENT`,
              name: `Auto SSL Certificate re-enabled`,
              uiSource: `Site Settings`,
            })
            trackSegment({
              type: SegmentEventType.Track,
              event: "Auto SSL Certificate re-enabled",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {showDomainForm && (
        <StyledPanelBoilerplate
          isOpen={showDomainForm}
          aria-label={text.headers.addDomain}
          onClose={() => setShowDomainForm(false)}
          header={text.headers.addDomain}
        >
          <StyledPanelBodySection>
            <GatsbyCloudCdnFeatures css={formDescriptionCss} />
            <AddDomainForm
              siteId={siteId}
              organizationId={organizationId}
              onComplete={() => {
                setShowDomainForm(false)

                onSuccess && onSuccess()

                trackAction({
                  eventType: `TRACK_EVENT`,
                  name: `Custom Domain added`,
                  uiSource: `Site Settings`,
                })
                trackSegment({
                  type: SegmentEventType.Track,
                  event: "Custom Domain added",
                  properties: {
                    location: "Site Settings",
                  },
                })
              }}
              onCancel={() => setShowDomainForm(false)}
            />
          </StyledPanelBodySection>
        </StyledPanelBoilerplate>
      )}
    </div>
  )
}
